import { useEffect, useRef } from 'react';

import { meterFraction } from '../audio/meter';

export interface PeakSample {
  left: number;
  right: number;
  holdL: number;
  holdR: number;
}

/**
 * Stereo meters fed by the `peakL`/`peakR` bytes in the frame header — no decoded
 * audio involved, which is exactly why those bytes are in the header.
 *
 * The bars are written straight to the DOM from a rAF loop instead of through
 * React state: at 50 frames a second a re-render per update would dominate the
 * page's work for something that is purely decorative.
 */
export function LevelMeters({ sample, active }: { sample(atMs: number): PeakSample; active: boolean }) {
  const leftFill = useRef<HTMLDivElement>(null);
  const rightFill = useRef<HTMLDivElement>(null);
  const leftHold = useRef<HTMLDivElement>(null);
  const rightHold = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const apply = (peaks: PeakSample) => {
      const set = (
        fill: HTMLDivElement | null,
        hold: HTMLDivElement | null,
        level: number,
        peak: number,
      ) => {
        if (fill) fill.style.clipPath = `inset(0 ${(100 - meterFraction(level) * 100).toFixed(2)}% 0 0)`;
        if (hold) hold.style.left = `${(meterFraction(peak) * 100).toFixed(2)}%`;
      };
      set(leftFill.current, leftHold.current, peaks.left, peaks.holdL);
      set(rightFill.current, rightHold.current, peaks.right, peaks.holdR);
    };

    if (!active) {
      apply({ left: 0, right: 0, holdL: 0, holdR: 0 });
      return;
    }

    let frame = 0;
    const tick = () => {
      apply(sample(performance.now()));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [sample, active]);

  return (
    <div className="meters" role="group" aria-label="Output level">
      <div className="meter">
        <span className="tag" aria-hidden="true">
          L
        </span>
        <div className="track" aria-hidden="true">
          <div className="fill" ref={leftFill} />
          <div className="hold" ref={leftHold} />
        </div>
      </div>
      <div className="meter">
        <span className="tag" aria-hidden="true">
          R
        </span>
        <div className="track" aria-hidden="true">
          <div className="fill" ref={rightFill} />
          <div className="hold" ref={rightHold} />
        </div>
      </div>
      <div className="scale" aria-hidden="true">
        <span>-60 dBFS</span>
        <span>0 dBFS</span>
      </div>
    </div>
  );
}
